import { Box, Button, Stack, Typography } from '@mui/material'

// ----------------------------------------------------------------------
// What is shown when something cannot render at all.
//
// The case this exists for is routine rather than exotic: every screen is a
// chunk fetched on demand with a hashed filename, so for a few minutes after a
// deploy an open tab asks for a chunk that no longer exists. Without a boundary
// that rejection unmounts the whole application and leaves a white window, and
// the operator's only clue is that the console stopped working.
//
// `screen` is the usual one, and it fills the content column so the sidebar
// stays there and the operator can go somewhere else without reloading.
// `app` covers the window, for the chunks that carry the chrome itself, where
// there is nothing left to navigate with.
//
// Nothing here is lazily loaded or fetched, because the reason we are here may
// be that loading does not work.
// ----------------------------------------------------------------------

type Props = {
  variant?: 'app' | 'screen'
}

const AppErrorFallback = ({ variant = 'app' }: Props) => (
  <Box
    sx={
      variant === 'app'
        ? {
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            bgcolor: 'background.default',
          }
        : {
            minHeight: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }
    }
  >
    <Stack spacing={2} alignItems="center" sx={{ maxWidth: 420 }}>
      <Typography variant="h3">This screen could not be loaded</Typography>
      <Typography variant="body2" color="text.secondary" align="center">
        {variant === 'app'
          ? 'Reloading usually fixes it. If it happens again, the version you have open is probably older than the one on the server.'
          : 'Reloading usually fixes it, and the rest of the console still works. If it happens again, the version you have open is probably older than the one on the server.'}
      </Typography>
      <Button variant="contained" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </Stack>
  </Box>
)

export default AppErrorFallback
